import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, PlusCircle, Pencil, Trash2, ExternalLink, Newspaper } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { getAdminBlogs, deleteBlog } from '../utils/api';

const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

const BlogAdmin = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    getAdminBlogs()
      .then((data) => setPosts(data || []))
      .catch((err) => toast.error('Failed to load posts: ' + err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    document.title = 'Manage Blog - Rubicon Finservices';
    load();
  }, []);

  const handleDelete = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    try {
      await deleteBlog(post.id);
      toast.success('Post deleted');
      load();
    } catch (err) {
      toast.error('Delete failed: ' + err.message);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="blog-admin-page">
      <header className="bg-white shadow-md">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Newspaper className="text-amber-600" size={28} />
            <div>
              <h1 className="text-xl font-bold text-slate-900">Manage Blog</h1>
              <p className="text-sm text-slate-600">Create, edit and publish articles</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin/dashboard">
              <Button variant="outline" size="sm" className="flex items-center gap-2" data-testid="blog-admin-back">
                <ArrowLeft size={16} /> Dashboard
              </Button>
            </Link>
            <Button onClick={() => navigate('/admin/blog/create')} className="bg-amber-600 hover:bg-amber-700 text-white flex items-center gap-2" data-testid="blog-admin-new">
              <PlusCircle size={18} /> New Post
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-600"></div>
          </div>
        ) : posts.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Newspaper className="mx-auto text-slate-300 mb-4" size={64} />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No posts yet</h3>
              <p className="text-slate-600 mb-6">Write your first blog article to publish it on the website.</p>
              <Button onClick={() => navigate('/admin/blog/create')} className="bg-amber-600 hover:bg-amber-700 text-white">
                Create Post
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4" data-testid="blog-admin-list">
            {posts.map((post) => (
              <Card key={post.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6 flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-3 mb-1">
                      <h3 className="text-lg font-bold text-slate-900 truncate">{post.title}</h3>
                      {post.published ? (
                        <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Published</Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-600 hover:bg-gray-100">Draft</Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-500">
                      /blog/{post.slug} · {post.category || 'Uncategorized'} · {formatDate(post.date)}
                    </p>
                    {post.excerpt && <p className="text-sm text-slate-600 mt-2 line-clamp-2">{post.excerpt}</p>}
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    {post.published && (
                      <a href={`/blog/${post.slug}`} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" size="sm" className="flex items-center gap-1" data-testid={`blog-view-${post.slug}`}>
                          <ExternalLink size={14} /> View
                        </Button>
                      </a>
                    )}
                    <Button variant="outline" size="sm" className="flex items-center gap-1" onClick={() => navigate(`/admin/blog/edit/${post.id}`)} data-testid={`blog-edit-${post.slug}`}>
                      <Pencil size={14} /> Edit
                    </Button>
                    <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700 flex items-center gap-1" onClick={() => handleDelete(post)} data-testid={`blog-delete-${post.slug}`}>
                      <Trash2 size={14} /> Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogAdmin;
